/**
 * Cross-component bridge for the Global Config (project settings) modal.
 *
 * - `openProjectSettings()` lets chat cards (project-setup card, global-config
 *   action card) open the modal owned by `ProjectConfigGear` without prop
 *   drilling. Several non-inline gears can be mounted at once (canvas dock +
 *   app header), so openers are kept in a stack and ONLY the most recently
 *   mounted one responds — exactly one modal ever opens.
 * - `notifyProjectConfigChanged()` / `subscribeProjectConfigChanged()` relay the
 *   backend `project_config_changed` chat event (e.g. a Director
 *   `update_project_config` call) to every `useProjectConfig` consumer so the
 *   gear and the chat cards refresh live.
 *
 * Feature tag: `project-setup-card`.
 */

import { createNadDebug } from '../../utils/debugLog'

const log = createNadDebug('dw:project-setup-card:settings-events')

export type ProjectSettingsOpenSource =
  | 'project_setup_card'
  | 'project_setup_card_body'
  | 'global_config_action_card'
  | 'global_config_action_card_body'
  | 'canvas_context_menu'

type OpenHandler = (source: ProjectSettingsOpenSource) => void

const openers: OpenHandler[] = []

/** Register a gear as a modal owner. Returns the unregister function. */
export function registerProjectSettingsOpener(handler: OpenHandler): () => void {
  openers.push(handler)
  return () => {
    const index = openers.lastIndexOf(handler)
    if (index >= 0) openers.splice(index, 1)
  }
}

/** Open the Global Config modal. Returns false when no gear is mounted. */
export function openProjectSettings(source: ProjectSettingsOpenSource): boolean {
  const handler = openers[openers.length - 1]
  log({ event: 'open_requested', source, handled: !!handler, opener_count: openers.length })
  if (!handler) return false
  handler(source)
  return true
}

export interface ProjectConfigChangedDetail {
  projectId: string
  canvasId: string
  changedFields: string[]
  actor: string | null
  /**
   * Cost classes the Director turned auto-confirm OFF for (the only direction
   * it may change; only the user turns it on). The toggles are client-owned
   * localStorage, so the workspace applies these itself.
   */
  autoConfirmOff?: AutoConfirmCostClass[]
}

export type AutoConfirmCostClass = 'image' | 'video'

type ConfigChangedHandler = (detail: ProjectConfigChangedDetail) => void

const configListeners = new Set<ConfigChangedHandler>()

export function subscribeProjectConfigChanged(handler: ConfigChangedHandler): () => void {
  configListeners.add(handler)
  return () => {
    configListeners.delete(handler)
  }
}

export function notifyProjectConfigChanged(detail: ProjectConfigChangedDetail): void {
  log({
    event: 'config_changed',
    project_id: detail.projectId,
    canvas_id: detail.canvasId,
    changed_fields: detail.changedFields.join(','),
    actor: detail.actor,
    auto_confirm_off: (detail.autoConfirmOff ?? []).join(','),
  })
  for (const listener of configListeners) listener(detail)
}

/** @internal — reset module state between tests. */
export function _resetProjectSettingsEventsForTests(): void {
  openers.length = 0
  configListeners.clear()
}
